import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import $ from "jquery";
import {BuildingSelector} from "./BuildingSelector";
import {Aquilon1ChartJS} from "./charts/Aquilon1";
import {Aquilon2ChartJS} from "./charts/Aquilon2";
import {Compass1ChartJS} from "./charts/Compass1";
import {Compass2ChartJS} from "./charts/Compass2";
import {ConcertoChartJS} from "./charts/Concerto";
import {Evergreen1ChartJS} from "./charts/Evergreen1";
import {Evergreen2ChartJS} from "./charts/Evergreen2";
import {HerculesChartJS} from "./charts/Hercules";
import {Hydra1ChartJS} from "./charts/Hydra1";
import {Hydra2ChartJS} from "./charts/Hydra2";
import {Hydra3ChartJS} from "./charts/Hydra3";
import {KhronosChartJS} from "./charts/Khronos";
import {MaestroChartJS} from "./charts/Maestro";
import {Occam1ChartJS} from "./charts/Occam1";
import {Occam2ChartJS} from "./charts/Occam2";
import {SiroccoChartJS} from "./charts/Sirocco";
import {ShotoChartJS} from "./charts/Shoto";
import {TritonChartJS} from "./charts/Triton";
import userCombinations from "/data/user_combinations.json";
import {ScrollingNumbers} from "./ScrollingNumbers";
import {DecisionLogs} from "./DecisionLogs";
import {Equipment} from "./Equipment";

export class Algorithm {

  constructor(machineName) {
    this.machineName = machineName;
    this.algorithmData = app.data.content.algorithms[machineName];
    this.$container = $(`.algorithm.view-mode-full[data-algorithm-machine-name="${machineName}"]`);
    this.$teaser = $(`.algorithms-selectors-container .algorithm.view-mode-teaser[data-algorithm-machine-name="${machineName}"]`);

    this.decisionLogs = null;
    this.equipment = null;

    this.isActive = false;

    this.weatherOverrides = false;

    this.chartJSClasses = {
      Aquilon1ChartJS,
      Aquilon2ChartJS,
      Compass1ChartJS,
      Compass2ChartJS,
      ConcertoChartJS,
      Evergreen1ChartJS,
      Evergreen2ChartJS,
      HerculesChartJS,
      Hydra1ChartJS,
      Hydra2ChartJS,
      Hydra3ChartJS,
      KhronosChartJS,
      MaestroChartJS,
      Occam1ChartJS,
      TritonChartJS,
      Occam2ChartJS,
      SiroccoChartJS,
      ShotoChartJS
    }

    this.charts = [];
    this.userCombinationData = userCombinations[machineName] ?? [];

    this.rangesMap = {};
    this.buildRangesMap();

    this.buildingSelector = new BuildingSelector(this.$container.find(".building-selector"));

    this.$kpis = this.$container.find(".kpi");
    this.currentKPIKey = null;
    this.currentKPIsModifiers = [];
  }

  // To be overriden in child if needed
  buildRangesMap() {

  }


  async init() {
    await this.initWidgets();
    this.initKPIs();
    this.initUserInputs();
    this.resetUserInputs();
    this.initEvents();
  }

  // To be overriden in child if needed
  initWidgets() {
    const readyPromises = [];

    this.$container.find(".widgets-container .widget").each((i, el) => {
      let widgetType = $(el).attr("data-widget-type");

      if (widgetType === "chartjs") {
        let widgetClass = $(el).attr("data-widget-class");
        this.charts[widgetClass] = new this.chartJSClasses[widgetClass]($(el));

        // Wrap each widget's "ready" event in a Promise so we can know when all of them are ready
        const readyPromise = new Promise((resolve) => {
          this.charts[widgetClass].on("ready", () => {
            this.charts[widgetClass].setPreferredUnit(app.data.cms_content.visitor_customization.unit);
            this.charts[widgetClass].setPreferredLanguage(app.data.cms_content.visitor_customization.language);
            resolve(); // Chart is ready
          });
        });

        readyPromises.push(readyPromise);
      } else if (widgetType === "decision_logs") {
        this.decisionLogs = new DecisionLogs($(el), app.data.content.algorithms[this.machineName], app.data.content.equipments);
        this.decisionLogs.setPreferredUnit(app.data.cms_content.visitor_customization.unit);
      } else if (widgetType === "equipment") {
        this.equipment = new Equipment($(el));
      }
    });

    // Return the Promise so we can continue
    return Promise.all(readyPromises).then(() => {
      // console.log("All charts are ready!");
      if (this.decisionLogs && this.equipment) {
        // Decision logs and equipment are always displayed together and the Decision logs is the master
        // So it will be easier for the Decision logs to control the equipment widget this way
        this.decisionLogs.equipment = this.equipment;
      }
    });
  }

  // To be overridden in child if needed
  initKPIs() {
    this.$kpis.each((i, el) => {
      const scrollingNumbers = new ScrollingNumbers($(el).find(".scrolling-numbers"));
      $(el).data("scrolling-numbers", scrollingNumbers);

      // If any value in this KPI's set has decimals, force the max decimal count on all of them
      const values = $(el).data("values") ?? {};
      let maxDecimals = 0;
      Object.values(values).forEach(v => {
        const n = v?.[app.i18n.lang];
        if (n == null) return;
        const decPart = String(n).split(".")[1];
        if (decPart) maxDecimals = Math.max(maxDecimals, decPart.length);
      });

      $(el).data("force-decimals", maxDecimals);
      $(el).attr("data-force-decimals", maxDecimals); // Just to show it in the console for easier debug
      if (maxDecimals > 0) {
        scrollingNumbers.setForcedDecimals(maxDecimals);
      }
    })
  }

  // To be overriden in child if needed
  resetKPIs() {
    this.currentKPIsModifiers = [];

    this.$kpis.each((i, el) => {
      const values = $(el).data("values");
      const value = values[this.currentKPIKey];
      if (!value) return;
      $(el).data("scrolling-numbers").setValue(value[app.i18n.lang] * (this.currentKPIsModifiers[i] ?? 1));
    })
  }

  // To be overriden in child if needed
  initUserInputs() {

  }

  // To be overriden in child if needed
  resetUserInputs() {

  }

  initEvents() {
    this.buildingSelector.on("buildingChanged", (type) => this.onBuildingChange(type));

    // Algorithm - teaser, handle open/close
    this.$teaser.each((index, el) => {
      const $el = $(el);
      $el.off("click").on("click", () => {
        app.audio.play("audio/ButtonOnClick_260423.wav", 1, true)

        const isActive = $el.is(".active");

        // Switch between explore text to drop text
        const $exploreText = $("#screen-2 .explore-algorithm-text");
        const $exploreChars = $exploreText.find("span:visible span");
        const $dropText = $("#screen-2 .drop-algorithm-text");
        const $dropChars = $dropText.find("span:visible span");

        // Avoid animating multiple times
        // Check visibility AND ensure we don't have the "is-animated" class
        if (!isActive && $exploreText.css("visibility") !== "hidden" && !$exploreText.is(".is-animated")) {
          $exploreText.addClass("is-animated");
          gsap.set([$exploreChars, $dropChars], {willChange: "transform, opacity, filter"});

          gsap.to($exploreChars, {
            autoAlpha: 0, filter: "blur(5px)", y: -15, stagger: 0.05, duration: 0.25, ease: "power2.in",
            onComplete: () => {
              gsap.set([$exploreText, $dropChars], {autoAlpha: 0});
              gsap.set($dropText, {autoAlpha: 1});
              gsap.fromTo($dropChars,
                {autoAlpha: 0, filter: "blur(5px)", y: 15},
                {
                  autoAlpha: 1, filter: "blur(0px)", y: 0, stagger: 0.1, duration: 0.6, ease: "power2.out",
                  onComplete: () => gsap.set([$exploreChars, $dropChars], {clearProps: "willChange"})
                }
              );
            }
          });
        }

        // Skip toggle if highlighted, but animation above still runs
        if ($el.is(".highlighted")) return;
        $el.toggleClass("active", !isActive);
      });
    });
  }

  selectKPIs(key, force = false) {
    if (key == null) return;

    // Send current kpi key to other screens only if it changed or if we force it (because the modifier changed for example)
    if (force || this.currentKPIKey !== key) {
      this.currentKPIKey = key;
      app.sendScreenBridgeMessage(`kpiChange ${this.machineName}|${this.currentKPIKey}`);
    }

    // Now let's the the value linked to that key for each KPI and assign that value
    this.$kpis.each((i, el) => {
      const values = $(el).data("values");
      const value = values[this.currentKPIKey];
      if (!value) return;

      let newValue = this.getKPIValue(i, value);
      // console.log("New KPI value =>", newValue);
      $(el).data("scrolling-numbers").setValue(newValue);

      let currentUnit = $(el).find(`.kpi-unit .${app.i18n.lang}`).text();
      let newUnit = this.getKPIUnit($(el), value);

      if (currentUnit !== newUnit) {
        $(el).find(`.kpi-unit .${app.i18n.lang}`).text(newUnit);
      }
    })
  }

  getKPIValue(i, value) {
    // Apply the modifier
    const raw = value[app.i18n.lang];
    let newValue = raw * (this.currentKPIsModifiers[i] ?? 1);

    // Round to the original value's decimal count to avoid float drift from the modifier.
    // The display layer pads trailing zeros up to the forced decimal count if set.
    const decPart = String(raw).split(".")[1];
    const decCount = decPart ? decPart.length : 0;
    newValue = Number(newValue.toFixed(decCount));

    return newValue;
  }

  getKPIUnit($el, value) {

    let unitsModifiersMap = {
      hours: {
        singular: {
          fr: "heure",
          en: "hour"
        },
        plural: {
          fr: "heures",
          en: "hours"
        }
      }
    }

    let currentUnit = $el.find(`.kpi-unit .${app.i18n.lang}`).text();

    // Handle modifiers

    // Here we handle the word "hour" to add or remove the "s" at the end
    if (currentUnit.startsWith("hour") || currentUnit.startsWith("heure")) {
      currentUnit = value[app.i18n.lang] <= 1 ? unitsModifiersMap.hours.singular[app.i18n.lang] : unitsModifiersMap.hours.plural[app.i18n.lang];
    }

    return currentUnit;
  }

  // To be overriden in child if needed
  onBuildingChange(type) {
    // Get the KPIs Modifiers for the algo + building type
    const kpisModifiers = this.algorithmData?.buildings?.[type]?.kpis_modifiers;

    if (kpisModifiers) {
      // Apply the modifiers if they exist
      // console.log(kpisModifiers);
      this.currentKPIsModifiers = kpisModifiers;

      // // Force update the KPIs
      this.selectKPIs(this.currentKPIKey, true);
    }

    // Send current algo and building to other screens
    app.sendScreenBridgeMessage(`buildingChange ${this.machineName}|${type}`);

    if (this.decisionLogs) {
      this.decisionLogs.update(this.currentKPIKey, this.buildingSelector.currentBuilding);
    }
  }

  start() {
    // Send current algo to other screens
    app.sendScreenBridgeMessage(`algoChange ${this.machineName}`);

    // Stop any narration from the previous algorithm; the new one starts in onTransitionEnded
    app.narration.clearAll();
    app.sendScreenBridgeMessage(`narration stop`);

    // this.currentKPIKey = null;
    if (this.decisionLogs) {
      this.decisionLogs.reset();
    }

    this.resetKPIs();
    this.buildingSelector.reset();
    this.resetUserInputs();

    for (const chart of Object.values(this.charts)) {
      chart.setAiEnabled(true);
    }

    if (!this.weatherOverrides) {
      // clear override
      app.sendScreenBridgeMessage(`weatherOverride none`);
    }

    this.isActive = true;
    // Here we could start the animation where the content is shown, can be overriden in child
  }

  stop() {
    if(!this.isActive) return;

    app.narration.clearAll();
    app.sendScreenBridgeMessage(`narration stop`);

    if (this.decisionLogs) {
      this.decisionLogs.stopLogs();
    }

    this.isActive = false;

    // clear override
    app.sendScreenBridgeMessage(`weatherOverride none`);
  }

  updateAllBuildingsEquipments(humanReadableState) {
    const buildingEquipments = this.algorithmData.buildings[this.buildingSelector.currentBuilding].equipments;
    if (Array.isArray(buildingEquipments)) {
      for (const id of buildingEquipments) {
        app.sendScreenBridgeMessage(`updateEquipmentsState ${id} ${humanReadableState}`);
      }
    }
  }

  onTransitionEnded() {
    // Action that is triggered once the algorithm screen is actually done transitioning and is visible on screen
    if (this.decisionLogs) {
      this.decisionLogs.start();
    }

    // Play narration once the algorithm screen is fully visible (isolated from FX, killed on algo change)
    // app.narration.play(`audio/narrations/ai-control/${app.i18n.lang}/${this.machineName}.wav`, 1.0, true);
    // Update on 2026-05-17 : We are now triggering the audio playback in the LED Screen using a screenbridge message
    app.sendScreenBridgeMessage(`narration ${app.i18n.lang} ${this.machineName}`);
  }

  reset() {
    this.currentKPIsModifiers = [];

    if (this.equipment) {
      this.equipment.reset();
    }
  }
}
